import { BreadcrumbListJsonLdScriptTag } from '@findy-code/yuffie/shared/json-ld';
import { LoadingScrim } from '@findy-code/yuffie/shared/loading-scrim';
import { MatchModal } from '@findy-code/yuffie/shared/modal/match-modal';
import { Nl2Br } from '@findy-code/yuffie/shared/nl2br';
import { Breadcrumbs } from '@findy-code/yuffie/ui/breadcrumbs';
import { Stack } from '@findy-code/yuffie/ui/stack';

import { CompanyImages, SectionLayoutBase } from '../../../common';
import type {
  FetchJobDescriptionDetailFragment,
  JobDescriptionSpeakerDeckOembedFragment,
} from '../../graphql';
import type { HandleClickLikeParams } from '../../types';
import { getJobDescriptionBreadCrumbs } from '../../utils';
import { CompanyAiUsage } from '../company-ai-usage';
import { CompanyInterviews } from '../company-interviews';
import { CompanyFindyToolsArticles } from '../findy-tools-articles';
import { JobDescriptionHeader } from '../job-description-header';
import { JobDescriptionTable } from '../job-description-table';
import { JobFeatures } from '../job-features';
import { JobHighlight } from '../job-highlight/job-highlight.component';
import { JsonLdTag } from '../json-ld-tag';
import { LikeButtonGroup } from '../like-button-group';
import { Members } from '../member';
import { Register } from '../register';
import { RequiredSkills } from '../required-skills';
import styles from '../styles.module.scss';
import { useCompanyJobDescriptionDetailPresenter } from './company-job-description-detail.presenter';

type Props = {
  isLoading: boolean;
  isLoadingCurrentUser?: boolean;
  isFirstLoaded: boolean;
  isLogin: boolean;
  jobDescription: FetchJobDescriptionDetailFragment;
  // @todo FragmentでなくTrustedHTMLに変更
  speakerDeckOembed?: JobDescriptionSpeakerDeckOembedFragment | null;
  isBlockCompany: boolean;
  // 確約面談か否かの判定フラグ
  isUserSuccessCommitmentJobInterview: boolean;
  // GitHub解析の結果で登録されたSkillのidリスト
  userSkillIds: number[];
  // 自己申告スキルに登録されたデータのidリスト
  userSelfRecommendSkillIds: number[];
  // ユーザが趣向性に登録しているidリスト
  userPersonalityIds: number[];
  isOpenMatchModal: boolean;
  user: {
    vision: string | null | undefined;
    latestDevelopmentResults: string | null | undefined;
  };
  jobDescriptionLike: {
    id: number;
    isKeep: boolean;
    likeHash: string;
    likeExpirationDate: string;
  } | null;
  userLike: { id: number } | null;
  match: { matchHash: string } | null;
  onClickLink?: (label: string) => void;
  onClickLike?: (params: HandleClickLikeParams) => void;
  onClickKeep?: (jdLikeHash: string, isDisabled: boolean) => void;
  onCloseMatchModal?: () => void;
};

/**
 * ユーザログインに描画する求人詳細ページ
 */
export const CompanyJobDescriptionDetail = ({
  isLoading,
  isLoadingCurrentUser,
  isFirstLoaded,
  isLogin,
  jobDescription,
  speakerDeckOembed,
  isOpenMatchModal,
  isUserSuccessCommitmentJobInterview,
  isBlockCompany,
  userSkillIds,
  userSelfRecommendSkillIds,
  userPersonalityIds,
  user,
  jobDescriptionLike,
  userLike,
  match,
  onClickLink,
  onClickLike,
  onClickKeep,
  onCloseMatchModal,
}: Props) => {
  const {
    jobDescriptionsCharacteristicsWithMatches,
    companyAiUsageLatestUpdatedAt,
  } = useCompanyJobDescriptionDetailPresenter({ jobDescription });

  const breadcrumbs = getJobDescriptionBreadCrumbs({
    companyId: jobDescription.company.id,
    companyName: jobDescription.company.name,
    jobDescriptionTitle: jobDescription.title,
    isLogin,
    onClickLink,
  });

  return (
    <LoadingScrim isLoading={isLoading || isLoadingCurrentUser}>
      {isLogin && (
        <MatchModal
          companyName={jobDescription.company.name}
          hasLatestDevelopmentResults={
            (user.latestDevelopmentResults ?? '').length > 0
          }
          hasVision={(user.vision ?? '').length > 0}
          isOpen={isOpenMatchModal}
          matchHash={match?.matchHash ?? ''}
          onClose={onCloseMatchModal}
        />
      )}
      <section className={styles.contentWrapper}>
        <Breadcrumbs items={breadcrumbs} />
        {!isLogin && (
          <BreadcrumbListJsonLdScriptTag
            items={breadcrumbs.map(({ name, link }, index) => {
              const isActive = breadcrumbs.length - 1 === index;
              return {
                name,
                link: isActive
                  ? `/companies/${jobDescription.company.id}/jobs/${jobDescription.jdHash}`
                  : link,
              };
            })}
          />
        )}
        <Stack spacing="x3l">
          <JobDescriptionHeader
            companyAiUsage={jobDescription.company.companyAiUsage}
            companyId={jobDescription.company.id}
            companyName={jobDescription.company.name}
            publishedAt={jobDescription.publishedAt}
            teamPlusAward={jobDescription.company.teamPlusAward}
            title={jobDescription.title}
            updatedAt={jobDescription.updatedAt}
          />

          <JobHighlight
            jobDescriptionsCharacteristics={
              jobDescriptionsCharacteristicsWithMatches
            }
            jobTypeName={jobDescription.jobType?.name ?? ''}
            managementJobTypeName={jobDescription.managementJobType?.name ?? ''}
            numericSalaryMax={jobDescription.numericSalaryMax}
            numericSalaryMin={jobDescription.numericSalaryMin}
            skills={jobDescription.skills}
            userSelfRecommendedSkillIds={userSelfRecommendSkillIds}
            userSkillIds={userSkillIds}
          />

          {jobDescription.company.companyAiUsage?.isUsingAi && (
            <SectionLayoutBase
              heading={{
                title: '生成AIの活用状況',
                comment: companyAiUsageLatestUpdatedAt,
              }}
              id="company-ai-usage"
            >
              <CompanyAiUsage
                companyAiUsage={jobDescription.company.companyAiUsage}
                companyId={jobDescription.company.id}
                isLogin={isLogin}
                jobDescriptionHash={jobDescription.jdHash}
                onClickLink={onClickLink}
              />
            </SectionLayoutBase>
          )}

          <SectionLayoutBase
            heading={{
              title: '仕事内容',
            }}
            id="job-description"
          >
            {isLogin ? (
              <pre className={styles.pre}>
                <Nl2Br addLink text={jobDescription.jobDescription ?? ''} />
              </pre>
            ) : (
              <Stack spacing="l">
                <pre className={styles.pre}>
                  <Nl2Br
                    addLink
                    text={
                      jobDescription.jobDescription.length > 100
                        ? jobDescription.jobDescription.slice(0, 100)
                        : jobDescription.jobDescription
                    }
                  />
                </pre>
                <Register
                  companyId={jobDescription.company.id}
                  jobDescriptionHash={jobDescription.jdHash}
                  onClickLink={onClickLink}
                />
              </Stack>
            )}
          </SectionLayoutBase>

          <CompanyImages
            companyName={jobDescription.company.name}
            images={jobDescription.company.images}
            jobTypeName={jobDescription.jobType?.name}
            managementJobTypeName={jobDescription.managementJobType?.name}
          />

          <SectionLayoutBase
            heading={{
              title: '開発環境',
            }}
            id="development-environment"
          >
            <pre className={styles.pre}>
              <Nl2Br
                addLink
                text={jobDescription.developmentEnvironment ?? ''}
              />
            </pre>
          </SectionLayoutBase>

          <SectionLayoutBase
            heading={{
              title: '求めるスキル',
            }}
            id="skills"
          >
            <RequiredSkills
              desiredSkills={jobDescription.desiredSkills ?? ''}
              mustSkills={jobDescription.mustSkills ?? ''}
              personality={jobDescription.personality ?? ''}
              rewarding={jobDescription.rewarding ?? ''}
              speakerDeckOembed={speakerDeckOembed}
            />
          </SectionLayoutBase>
          <SectionLayoutBase
            heading={{
              title: '募集条件',
            }}
            id="job-description-table"
          >
            {isLogin ? (
              <JobDescriptionTable jobDescription={jobDescription} />
            ) : (
              <Register
                companyId={jobDescription.company.id}
                jobDescriptionHash={jobDescription.jdHash}
                onClickLink={onClickLink}
              />
            )}
          </SectionLayoutBase>

          {jobDescription.companiesMembers.length > 0 && (
            <SectionLayoutBase
              heading={{
                title: 'メンバー',
              }}
              id="member"
            >
              <Members members={jobDescription.companiesMembers} />
            </SectionLayoutBase>
          )}

          {jobDescription.company.publishedInterviews.length > 0 && (
            <CompanyInterviews
              interviews={jobDescription.company.publishedInterviews}
              onClick={onClickLink}
            />
          )}

          {jobDescription.company.publishedFindyToolsArticles.length > 0 && (
            <CompanyFindyToolsArticles
              articles={jobDescription.company.publishedFindyToolsArticles}
            />
          )}

          <SectionLayoutBase
            heading={{
              title: '求人の特徴',
            }}
            id="job-features"
          >
            <JobFeatures
              isLogin={isLogin}
              jobDescriptionsCharacteristicsWithMatches={
                jobDescriptionsCharacteristicsWithMatches
              }
              skills={jobDescription.skills}
              tags={jobDescription.tags}
              userPersonalityIds={userPersonalityIds}
              userSelfRecommendSkillIds={userSelfRecommendSkillIds}
              userSkillIds={userSkillIds}
            />
          </SectionLayoutBase>

          {isLogin && isFirstLoaded && (
            <LikeButtonGroup
              company={{
                name: jobDescription.company.name,
                id: jobDescription.company.id,
              }}
              hasMatch={!!match}
              hasReceivedLike={!!jobDescriptionLike?.id}
              isBlockCompany={isBlockCompany}
              isKeep={jobDescriptionLike?.isKeep ?? false}
              isSent={!!userLike?.id}
              isUserSuccessCommitmentJobInterview={
                isUserSuccessCommitmentJobInterview
              }
              jobDescription={jobDescription}
              jobDescriptionLike={jobDescriptionLike}
              matchHash={match?.matchHash ?? ''}
              onClickKeep={onClickKeep}
              onClickLike={onClickLike}
            />
          )}

          {!isLogin && (
            <JsonLdTag
              company={jobDescription.company}
              jobDescription={jobDescription}
            />
          )}
        </Stack>
      </section>
    </LoadingScrim>
  );
};
